<template>
  <section class="staking-form-section">
    <h2 class="section-title">
      <span>质押RAD获取BOX奖励</span>
      <div class="share-wrap">
        分享
        <span class="iconfont icon-fenxiang share-btn" @click="onShare" />
      </div>
    </h2>
    <div class="form-card">
      <div
        v-for="option in durationOptions"
        :key="option.value"
        class="period-block"
      >
        <div class="period-header">
          <span class="period-label">{{ option.label }}</span>
          <div class="progress-wrap">
            <div class="progress-track">
              <div
                class="progress-fill"
                :style="{ width: `${getQuota(option.value).progress}%` }"
              />
            </div>
            <span class="progress-text">{{ getQuota(option.value).progress }}%</span>
          </div>
        </div>

        <div class="quota-row">
          <span class="quota-text">
            总额度: {{ getQuota(option.value).total }}RAD
          </span>
          <span class="quota-text">
            剩余额度: {{ getQuota(option.value).remaining }}RAD
          </span>
        </div>

        <div class="stake-row">
          <div class="input-wrap">
            <input
              v-model="amounts[option.value]"
              type="number"
              class="form-input"
              :placeholder="amountPlaceholder"
              :disabled="isSubmitting(option.value)"
            />
            <div class="input-suffix">
              <span class="unit">RAD</span>
              <img src="@/assets/image/radicon.jpg" alt="RAD" class="token-icon" />
            </div>
          </div>
          <button
            class="stake-btn"
            :class="{ loading: isSubmitting(option.value) }"
            :disabled="isSubmitting(option.value)"
            @click="onStake(option.value)"
          >
            {{ isSubmitting(option.value) ? "提交中..." : "质押" }}
          </button>
        </div>
      </div>

      <p class="referral">上级地址: {{ displayParentAddress }}</p>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { Notify } from "vant";
import { RAD_STAKE_DURATION_OPTIONS } from "../config";
import { shortenAddress, buildRadStakeInviteUrl, copyText } from "@/utils/utils";

export default {
  props: {
    parentAddress: {
      type: String,
      default: "",
    },
    minStake: {
      type: String,
      default: "",
    },
    periodQuotas: {
      type: Object,
      default: () => ({}),
    },
    submittingPeriod: {
      type: [Number, String],
      default: "",
    },
  },
  data() {
    return {
      durationOptions: RAD_STAKE_DURATION_OPTIONS,
      amounts: {
        180: "",
        360: "",
        720: "",
      },
    };
  },
  computed: {
    ...mapGetters(["accountAddress"]),
    displayParentAddress() {
      if (!this.parentAddress) {
        return "未绑定";
      }
      return shortenAddress(this.parentAddress);
    },
    amountPlaceholder() {
      if (this.minStake) {
        return `请输入质押数量, 最小${this.minStake} RAD`;
      }
      return "请输入质押数量";
    },
  },
  methods: {
    getQuota(period) {
      return (
        this.periodQuotas[period] || {
          staked: "0",
          total: "0",
          remaining: "0",
          progress: 0,
        }
      );
    },
    isSubmitting(period) {
      return this.submittingPeriod === period;
    },
    onStake(period) {
      if (this.isSubmitting(period)) return;
      this.$emit("submit", {
        amount: this.amounts[period],
        period,
      });
    },
    async onShare() {
      if (!this.accountAddress) {
        Notify({ type: "warning", message: "请先连接钱包后再分享" });
        return;
      }
      const inviteUrl = buildRadStakeInviteUrl(this.accountAddress);
      const copied = await copyText(inviteUrl);
      if (copied) {
        Notify({ type: "success", message: "推广链接已复制" });
      } else {
        Notify({ type: "warning", message: "复制失败，请手动复制链接" });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.staking-form-section {
  padding: 0 30px 24px;
}

.section-title {
  margin: 0 0 16px;
  font-size: 30px;
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .share-wrap {
    color: #7338ff;
    display: flex;
    align-items: center;
    font-size: 26px;
  }

  .share-btn {
    font-size: 40px;
    cursor: pointer;
    transition: opacity 0.2s;

    &:active {
      opacity: 0.7;
    }
  }
}

.form-card {
  padding: 20px;
  border-radius: 16px;
  background: #040a28;
}

.period-block {
  padding-bottom: 30px;
  margin-bottom: 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  &:last-of-type {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
}

.period-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.period-label {
  flex-shrink: 0;
  font-size: 26px;
  color: rgba(255, 255, 255, 0.85);
}

.progress-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  justify-content: flex-end;
}

.progress-track {
  flex: 1;
  max-width: 200px;
  height: 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  background: #7338ff;
  transition: width 0.3s ease;
}

.progress-text {
  flex-shrink: 0;
  font-size: 22px;
  color: rgba(255, 255, 255, 0.7);
}

.quota-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.quota-text {
  font-size: 22px;
  color: rgba(255, 255, 255, 0.5);
}

.stake-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.input-wrap {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  height: 72px;
  padding: 0 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.form-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 26px;
  color: #fff;

  &::placeholder {
    color: rgba(255, 255, 255, 0.35);
  }
}

.input-suffix {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 12px;

  .unit {
    font-size: 26px;
    color: rgba(255, 255, 255, 0.7);
  }

  .token-icon {
    width: 42px;
    height: 42px;
    object-fit: contain;
    border-radius: 50%;
  }
}

.stake-btn {
  flex-shrink: 0;
  min-width: 180px;
  height: 62px;
  padding: 0 24px;
  border: none;
  border-radius: 36px;
  font-size: 28px;
  color: #fff;
  background: #7338ff;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:active:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.referral {
  margin: 20px 0 0;
  font-size: 24px;
  color: rgba(255, 255, 255, 0.5);
}
</style>
